<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import type {
  ISatPlaceSearcherEmits,
  ISatButtonCurrentLocationEmits,
} from "@chatfood/design-system";
import {
  SatPlaceSearcher,
  SatButtonCurrentLocation,
} from "@chatfood/design-system";
import { ref, watch } from "vue";
import { useLocale } from "~/composables/use-locale";
import { usePublicConfig } from "~/config";
import { useBusinessStore } from "~/stores/business";
import type { INewAddressProps } from "./props";
import type { INewAddressEmits } from "./emits";

const css = bemBuilder("new-address");

const props = defineProps<INewAddressProps>();
const emits = defineEmits<INewAddressEmits>();

const { t } = useLocale();
const { googleMapsApiKey } = usePublicConfig();

const business = useBusinessStore();

const initialValue = ref(props.activeAddress ?? "");
watch(
  () => props.activeAddress,
  (val) => {
    initialValue.value = val ?? "";
  },
);

const onSelect = (place: ISatPlaceSearcherEmits["select"][0]) => {
  emits("select", place);
};

const onCurrentLocation = (
  location: ISatButtonCurrentLocationEmits["success"][0],
) => {
  emits("select", location);
};
</script>

<template>
  <div :class="css('')">
    <SatPlaceSearcher
      :api-key="googleMapsApiKey"
      :country="business.country"
      :input="initialValue"
      :max="4"
      :placeholder="t('components.new_address.search_input_placeholder')"
      @select="onSelect"
    />
    <SatButtonCurrentLocation
      :class="css('current-location')"
      :label="t('components.new_address.current_location')"
      :wide="true"
      :api-key="googleMapsApiKey"
      @success="onCurrentLocation"
    />
  </div>
</template>

<style lang="scss" scoped>
.new-address {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
</style>
